/**
 * Grafana Faro Monitoring Composable
 * Provides a clean API for tracking custom events and errors in the Customer UI
 */

declare global {
  interface Window {
    GrafanaFaroWebSdk?: {
      faro?: {
        api: {
          pushEvent: (name: string, attributes?: Record<string, any>, type?: string) => void;
          pushError: (error: Error, context?: Record<string, any>) => void;
          pushMeasurement: (measurement: { type: string; values: Record<string, any> }) => void;
          setUser: (user: { id: string; email?: string; username?: string }) => void;
        };
      };
    };
  }
}

export function useMonitoring() {
  /**
   * Check if Faro SDK is available
   */
  const isAvailable = (): boolean => {
    return !!(window.GrafanaFaroWebSdk?.faro);
  };

  /**
   * Track a custom event
   * @param name - Event name (e.g., "payment_initiated")
   * @param attributes - Additional event attributes/context
   */
  const trackEvent = (name: string, attributes?: Record<string, any>): void => {
    if (isAvailable()) {
      window.GrafanaFaroWebSdk!.faro!.api.pushEvent(name, attributes, 'custom');
    } else {
      console.debug(`[Monitoring] Would track event: ${name}`, attributes);
    }
  };

  /**
   * Track an error
   * @param error - Error object or message
   * @param context - Additional context about the error
   */
  const trackError = (error: Error | string, context?: Record<string, any>): void => {
    const errorObj = typeof error === 'string' ? new Error(error) : error;

    if (isAvailable()) {
      window.GrafanaFaroWebSdk!.faro!.api.pushError(errorObj, context);
    } else {
      console.error('[Monitoring] Error:', errorObj, context);
    }
  };

  /**
   * Set the current user context
   * @param userId - User identifier
   * @param email - User email (optional)
   * @param username - User username (optional)
   */
  const setUser = (userId: string, email?: string, username?: string): void => {
    if (isAvailable()) {
      window.GrafanaFaroWebSdk!.faro!.api.setUser({
        id: userId,
        email,
        username,
      });
    } else {
      console.debug('[Monitoring] Would set user:', { userId, email, username });
    }
  };

  /**
   * Track a page view (manual, if needed beyond automatic tracking)
   * @param pageName - Name of the page
   * @param metadata - Additional page metadata
   */
  const trackPageView = (pageName: string, metadata?: Record<string, any>): void => {
    if (isAvailable()) {
      window.GrafanaFaroWebSdk!.faro!.api.pushMeasurement({
        type: 'page_view',
        values: {
          name: pageName,
          ...metadata,
        },
      });
    } else {
      console.debug('[Monitoring] Would track page view:', pageName, metadata);
    }
  };

  // Customer-specific event tracking helpers

  /**
   * Track user registration
   */
  const trackUserRegistration = (data: {
    mailCenterId?: string;
    source?: string;
  }): void => {
    trackEvent('user_registration', data);
  };

  /**
   * Track mailbox activation
   */
  const trackMailboxActivation = (data: {
    mailboxId: string;
    mailCenterId: string;
    planId?: string;
  }): void => {
    trackEvent('mailbox_activation', data);
  };

  /**
   * Track payment events
   */
  const trackPaymentInitiated = (data: {
    amount: number;
    currency?: string;
    mailboxId?: string;
  }): void => {
    trackEvent('payment_initiated', data);
  };

  const trackPaymentSuccess = (data: {
    amount: number;
    currency?: string;
    transactionId?: string;
    mailboxId?: string;
  }): void => {
    trackEvent('payment_success', data);
  };

  const trackPaymentFailed = (data: {
    amount: number;
    currency?: string;
    reason?: string;
    mailboxId?: string;
  }): void => {
    trackEvent('payment_failed', data);
  };

  /**
   * Track mail actions
   */
  const trackMailScanRequested = (data: {
    mailId: string;
    mailboxId: string;
  }): void => {
    trackEvent('mail_scan_requested', data);
  };

  const trackMailForwardRequested = (data: {
    mailId: string;
    mailboxId: string;
    destination?: string;
  }): void => {
    trackEvent('mail_forward_requested', data);
  };

  const trackMailShredRequested = (data: {
    mailId: string;
    mailboxId: string;
  }): void => {
    trackEvent('mail_shred_requested', data);
  };

  const trackMailDisposeRequested = (data: {
    mailId: string;
    mailboxId: string;
  }): void => {
    trackEvent('mail_dispose_requested', data);
  };

  /**
   * Track USPS form submission
   */
  const trackUSPSFormSubmitted = (data: {
    formType: string;
    mailboxId?: string;
  }): void => {
    trackEvent('usps_form_submitted', data);
  };

  /**
   * Track address change
   */
  const trackAddressChangeSubmitted = (data: {
    mailboxId: string;
  }): void => {
    trackEvent('address_change_submitted', data);
  };

  /**
   * Track user login
   */
  const trackUserLogin = (data: {
    method?: string;
    userId?: string;
  }): void => {
    trackEvent('user_login', data);
  };

  /**
   * Track user logout
   */
  const trackUserLogout = (): void => {
    trackEvent('user_logout');
  };

  return {
    // Core tracking functions
    trackEvent,
    trackError,
    setUser,
    trackPageView,
    isAvailable,

    // Customer-specific helpers
    trackUserRegistration,
    trackMailboxActivation,
    trackPaymentInitiated,
    trackPaymentSuccess,
    trackPaymentFailed,
    trackMailScanRequested,
    trackMailForwardRequested,
    trackMailShredRequested,
    trackMailDisposeRequested,
    trackUSPSFormSubmitted,
    trackAddressChangeSubmitted,
    trackUserLogin,
    trackUserLogout,
  };
}